<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { PermissionSubject } from '@/features/FormBuilder';
  import { Checkbox, Input } from '@/shared/ui';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';

  const lab = useMatrixFillLabContext();
  const positionFilter = ref('');
  const activityFilter = ref('');

  function filterBy(list: PermissionSubject[], term: string): PermissionSubject[] {
    const needle = term.trim().toLowerCase();
    return needle ? list.filter((item) => item.name.toLowerCase().includes(needle)) : list;
  }

  const positions = computed(() => filterBy(lab.subjects.positions.value, positionFilter.value));
  const activities = computed(() => filterBy(lab.subjects.functionalActivities.value, activityFilter.value));

  function toggle(list: number[], id: number): number[] {
    return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
  }

  function clear(): void {
    lab.selectedPositionIds.value = [];
    lab.selectedActivityIds.value = [];
  }
</script>

<template>
  <div
    class="flex flex-col gap-2 rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-02)] p-2"
    data-testid="subject-picker"
  >
    <div class="grid grid-cols-2 gap-2">
      <div class="flex min-w-0 flex-col gap-1">
        <p class="font-semibold">
          Cargos
        </p>
        <Input
          v-model="positionFilter"
          placeholder="Filtrar cargos"
          class="h-7 bg-white text-xs md:text-xs"
        />
        <p
          v-if="lab.subjects.hasPositionsError.value"
          class="text-red-600"
        >
          Falha ao carregar cargos.
        </p>
        <div class="max-h-40 overflow-auto rounded-md border border-[var(--color-gray-02)] bg-white p-1.5">
          <label
            v-for="position in positions"
            :key="position.id"
            class="flex cursor-pointer items-center gap-2 py-0.5"
          >
            <Checkbox
              :id="`subject-position-${position.id}`"
              :checked="lab.selectedPositionIds.value.includes(position.id)"
              @toggle="lab.selectedPositionIds.value = toggle(lab.selectedPositionIds.value, position.id)"
            />
            <span class="truncate">{{ position.name }}</span>
          </label>
          <p
            v-if="positions.length === 0"
            class="italic text-[var(--color-gray-05)]"
          >
            Nenhum cargo.
          </p>
        </div>
      </div>
      <div class="flex min-w-0 flex-col gap-1">
        <p class="font-semibold">
          Atividades funcionais
        </p>
        <Input
          v-model="activityFilter"
          placeholder="Filtrar atividades"
          class="h-7 bg-white text-xs md:text-xs"
        />
        <p
          v-if="lab.subjects.hasFunctionalActivitiesError.value"
          class="text-red-600"
        >
          Falha ao carregar atividades.
        </p>
        <div class="max-h-40 overflow-auto rounded-md border border-[var(--color-gray-02)] bg-white p-1.5">
          <label
            v-for="activity in activities"
            :key="activity.id"
            class="flex cursor-pointer items-center gap-2 py-0.5"
          >
            <Checkbox
              :id="`subject-activity-${activity.id}`"
              :checked="lab.selectedActivityIds.value.includes(activity.id)"
              @toggle="lab.selectedActivityIds.value = toggle(lab.selectedActivityIds.value, activity.id)"
            />
            <span class="truncate">{{ activity.name }}</span>
          </label>
          <p
            v-if="activities.length === 0"
            class="italic text-[var(--color-gray-05)]"
          >
            Nenhuma atividade.
          </p>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between gap-2">
      <label class="flex cursor-pointer items-center gap-2">
        <Checkbox
          id="subject-default-closed"
          :checked="lab.defaultAccess.value === 'closed'"
          @toggle="lab.defaultAccess.value = lab.defaultAccess.value === 'closed' ? 'open' : 'closed'"
        />
        Campo sem permissão configurada fica fechado para todo mundo
      </label>
      <button
        type="button"
        class="shrink-0 text-[var(--color-gray-05)] underline"
        @click="clear"
      >
        Limpar seleção
      </button>
    </div>
  </div>
</template>
